import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["hoverContent", "requiredCellProps", "rightSlot", "as"];
import FormattedPropertyV2 from 'customer-data-properties/formatted/FormattedPropertyV2';
import { COMPANY_TYPE_ID, CONTACT_TYPE_ID, DEAL_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { getObjectLabel } from 'customer-data-objects/objects/getObjectLabel';
import { useCallback, memo, useMemo } from 'react';
import getIn from 'transmute/getIn';
import { getRecordHref } from '../../utils/getRecordHref';
import { AvatarCell } from '../AvatarCell';
import EmptyState from '../EmptyState';
import TruncateDiv from '../TruncateDiv';
import DealSplitIcon from './DealSplitIcon';
import UITableHoverCell from 'UIComponents/table/UITableHoverCell';
import { shouldRightAlignProperty } from '../../utils/shouldRightAlignProperty';
import { useIsDealSplitsEnabled } from '../../table/hooks/useIsDealSplitsEnabled';
import { useLogger } from '../../logger/context/LoggerContext';
import { alwaysUndefined } from '../../utils/alwaysUndefined';
import { CRM_PROPERTY_DATE_FORMAT, CRM_PROPERTY_DATE_TIME_FORMAT } from '../properties/constants';
import RecordLink from 'crm-links/record/RecordLink';
import { gate } from 'hub-http/gates';
import { useAuth } from '../../auth/context/AuthContext';
import withGateOverride from '../../utils/withGateOverride';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEAL_SPLIT_COUNT_PROPERTY_NAME = 'hs_num_associated_deal_splits';
const getIcons = (objectTypeId, object, isDealSplitsEnabled) => {
  if (objectTypeId === DEAL_TYPE_ID && Number(getIn(['properties', DEAL_SPLIT_COUNT_PROPERTY_NAME, 'value'], object)) > 1 && isDealSplitsEnabled) {
    return /*#__PURE__*/_jsx(DealSplitIcon, {});
  }
  return null;
};
export const DefaultLinkComponent = ({
  href,
  role,
  onClick,
  children
}) => {
  return /*#__PURE__*/_jsx(Link, {
    href: href,
    role: role,
    onClick: onClick,
    children: children
  });
};
const FormattedPropertyContent = /*#__PURE__*/memo(({
  objectTypeId,
  objectId,
  propertyDefinition,
  value,
  getSecondaryPropertyValue,
  icons
}) => {
  if ([CONTACT_TYPE_ID, COMPANY_TYPE_ID].includes(objectTypeId)) {
    return /*#__PURE__*/_jsx(AvatarCell, {
      type: objectTypeId === CONTACT_TYPE_ID ? 'vid' : 'companyId',
      id: objectId,
      displayName: value,
      children: value || /*#__PURE__*/_jsx(EmptyState, {})
    });
  }
  if (propertyDefinition) {
    return /*#__PURE__*/_jsxs(TruncateDiv, {
      rightAligned: shouldRightAlignProperty(propertyDefinition),
      children: [/*#__PURE__*/_jsx(FormattedPropertyV2, {
        objectTypeId: objectTypeId,
        objectId: objectId,
        property: propertyDefinition,
        value: value,
        getDraftValue: getSecondaryPropertyValue,
        getSavedValue: alwaysUndefined,
        dateFormat: CRM_PROPERTY_DATE_FORMAT,
        dateTimeFormat: CRM_PROPERTY_DATE_TIME_FORMAT,
        autoManageHighlySensitiveValue: false
      }), icons]
    });
  }
  return /*#__PURE__*/_jsx(EmptyState, {});
});
const LabelCellContent = ({
  object,
  typeDef,
  propertyDefinition,
  onClick,
  getSecondaryPropertyValue,
  LinkComponent = DefaultLinkComponent
}) => {
  const {
    objectId
  } = object;
  const {
    objectTypeId
  } = typeDef;
  const logger = useLogger();
  const recordHref = getRecordHref(objectTypeId, objectId);
  const isDealSplitsEnabled = useIsDealSplitsEnabled(objectTypeId);

  // HACK: Technically this is a formatted name for contacts, but in all other
  // cases it is actually the value of primaryDisplayLabelPropertyName
  const value = getObjectLabel(typeDef, object);
  const onClickWithLogger = useCallback((...args) => {
    logger({
      action: 'label-cell-link-clicked',
      data: {
        objectTypeId,
        objectId
      }
    });
    if (onClick) {
      onClick(...args);
    }
  }, [logger, objectTypeId, onClick, objectId]);

  // Check if a custom LinkComponent is provided (different from DefaultLinkComponent)
  const isCustomLinkComponent = LinkComponent !== DefaultLinkComponent;

  // Check feature flag
  const {
    hasGate
  } = useAuth();
  const hasRecordLinkGate = withGateOverride('CRM:Grid:UseRecordLinkInLabelCell', hasGate(gate('CRM:Grid:UseRecordLinkInLabelCell')));

  // Calculate icons for non-contact/company objects with property definition
  const icons = propertyDefinition ? getIcons(objectTypeId, object, isDealSplitsEnabled) : null;

  // Memoize LinkComponentProps
  const linkComponentProps = useMemo(() => Object.assign({
    onClick: onClickWithLogger
  }, [CONTACT_TYPE_ID, COMPANY_TYPE_ID].includes(objectTypeId) ? {} : {
    truncate: true,
    role: 'link'
  }), [onClickWithLogger, objectTypeId]);

  // Memoize FormattedPropertyContent props
  const formattedPropertyProps = useMemo(() => ({
    objectTypeId,
    objectId,
    propertyDefinition,
    value,
    getSecondaryPropertyValue,
    icons
  }), [objectTypeId, objectId, propertyDefinition, value, getSecondaryPropertyValue, icons]);
  if (isCustomLinkComponent || !hasRecordLinkGate) {
    // This is the original behavior using getRecordHref
    if ([CONTACT_TYPE_ID, COMPANY_TYPE_ID].includes(objectTypeId)) {
      return /*#__PURE__*/_jsx(AvatarCell
      // pass in link component to ensure expected behavior
      , {
        LinkComponent: LinkComponent,
        type: objectTypeId === CONTACT_TYPE_ID ? 'vid' : 'companyId',
        id: objectId,
        href: recordHref,
        onClick: onClickWithLogger,
        displayName: value,
        children: value || /*#__PURE__*/_jsx(EmptyState, {})
      });
    }
    if (propertyDefinition) {
      return /*#__PURE__*/_jsx(LinkComponent, {
        role: "link",
        href: recordHref,
        onClick: onClickWithLogger,
        children: /*#__PURE__*/_jsxs(TruncateDiv, {
          rightAligned: shouldRightAlignProperty(propertyDefinition),
          children: [/*#__PURE__*/_jsx(FormattedPropertyV2, {
            objectTypeId: objectTypeId,
            objectId: objectId,
            property: propertyDefinition,
            value: value,
            getDraftValue: getSecondaryPropertyValue,
            getSavedValue: alwaysUndefined,
            dateFormat: CRM_PROPERTY_DATE_FORMAT,
            dateTimeFormat: CRM_PROPERTY_DATE_TIME_FORMAT,
            autoManageHighlySensitiveValue: false
          }), icons]
        })
      });
    }
    return /*#__PURE__*/_jsx(EmptyState, {});
  }
  return /*#__PURE__*/_jsx(RecordLink, {
    objectTypeId: objectTypeId,
    objectId: Number(objectId),
    LinkComponentProps: linkComponentProps,
    children: /*#__PURE__*/_jsx(FormattedPropertyContent, Object.assign({}, formattedPropertyProps))
  });
};
const LabelCellContentWrapper = /*#__PURE__*/memo(({
  children,
  hasRightSlot
}) => {
  return hasRightSlot ? /*#__PURE__*/_jsx(Flex, {
    alignItems: "center",
    children: children
  }) : /*#__PURE__*/_jsx(View, {
    children: children
  });
});
const LabelCell = _ref => {
  let {
      hoverContent,
      requiredCellProps,
      rightSlot,
      as: WrapperElement = 'td'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    'data-onboarding': dataOnboarding,
    'data-table-external-id': dataTableExternalId,
    'data-test-id': dataTestId,
    'data-frozen-column': dataFrozenColumn,
    onContextMenu,
    style
  } = requiredCellProps;
  return /*#__PURE__*/_jsx(UITableHoverCell, {
    as: WrapperElement,
    "data-frozen-column": dataFrozenColumn,
    "data-onboarding": dataOnboarding,
    "data-table-external-id": dataTableExternalId,
    "data-test-id": dataTestId,
    hoverContent: hoverContent
    // @ts-expect-error this is a valid prop
    ,
    onContextMenu: onContextMenu,
    style: style,
    children: /*#__PURE__*/_jsxs(LabelCellContentWrapper, {
      hasRightSlot: Boolean(rightSlot),
      children: [/*#__PURE__*/_jsx(LabelCellContent, Object.assign({}, rest)), rightSlot]
    })
  });
};
export default LabelCell;